// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { sGetRaw } from "../lib/scopedStorage";
import type { SupabaseStorageProvider } from "../lib/supabaseStorage";
import type { UserProfile } from "../types";
import { DARK_THEME, LIGHT_THEME } from "../lib/theme";
import {
  computeDivisionTotals, divisionSpread, readRate, unionCostCodes,
  type DivisionTotal, type Spread,
} from "../lib/divisionCompare";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

type Metric = "costPerSF" | "total" | "share";

/** Share is a ratio, so it has no meaning for a whole project -- every project
 *  is 100% of itself. The toggle hides it on the Overview rather than offering
 *  a choice that would render a column of 100%. */
const METRIC_OPTIONS: Array<{ key: Metric; label: string; views: string[] }> = [
  { key: "costPerSF", label: "$/SF",      views: ["overview", "divisions", "detail"] },
  { key: "total",     label: "Total $",   views: ["overview", "divisions", "detail"] },
  { key: "share",     label: "% of Job",  views: ["divisions", "detail"] },
];

function fmtPct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}

/** One place that knows what each metric means, so a new metric cannot quietly
 *  fall through a `costPerSF ? a : b` branch and render dollars instead. */
function metricValue(metric: Metric, v: { costPerSF: number; total: number; jobSubtotal: number }): number {
  if (metric === "costPerSF") return v.costPerSF;
  if (metric === "total") return v.total;
  return v.jobSubtotal > 0 ? v.total / v.jobSubtotal : 0;
}

function fmtMetric(metric: Metric, n: number): string {
  if (metric === "costPerSF") return fmtSF(n);
  if (metric === "total") return fmt(n);
  return fmtPct(n);
}
const BAR_COLORS = ["#4fd1c5", "#68d391", "#f6e05e", "#fc8181", "#b794f4", "#76e4f7"];

// ── Helpers ──────────────────────────────────────────────────────────────────

function walkItems(rows: any[]): string[] {
  const ids: string[] = [];
  for (const row of rows || []) {
    if (row.type === "item" || (!row.type && row.id)) ids.push(row.id);
    if (row.rows) ids.push(...walkItems(row.rows));
    if (row.children) ids.push(...walkItems(row.children));
  }
  return ids;
}

function fmt(n: number) {
  if (!n) return "$0";
  const abs = Math.abs(Math.round(n));
  const s = "$" + abs.toLocaleString();
  return n < 0 ? `(${s})` : s;
}

function fmtSF(n: number) {
  return "$" + n.toFixed(0) + "/SF";
}

/** Whole dollars lose the number entirely once a rate drops near zero: a cost
 *  code at $2.49/SF and one at $1.51/SF both print as "$2/SF". Drill-down rows
 *  are small by nature, so they get the cents. */
function fmtSFPrecise(n: number) {
  if (n === 0) return "—";
  return "$" + n.toFixed(n < 10 ? 2 : 0) + "/SF";
}

function fmtArea(n: number) {
  return n ? n.toLocaleString() + " SF" : "—";
}

function pct(n: number) {
  return n ? n.toFixed(1) + "%" : "0%";
}

// ── Types ────────────────────────────────────────────────────────────────────

interface ProjectBenchmark {
  id: string;
  name: string;
  total: number;
  costPerSF: number;
  buildingArea: number;
  savedAt: string;
  selected: boolean;
  loaded: boolean;
  loading: boolean;
  divTotals: Record<string, DivisionTotal>;
  overhead: number;
  profit: number;
  contingency: number;
  subtotal: number;
}

/** A project as Division Detail sees it: the whole benchmark plus this one
 *  division's numbers and the cost codes underneath them. */
type DetailRow = ProjectBenchmark & {
  divTotal: number;
  divCostPerSF: number;
  pctOfSubtotal: number;
  subs: DivisionTotal["subs"];
};

interface Props {
  storage: SupabaseStorageProvider;
  user: UserProfile;
  onBack: () => void;
  // When hosted as a tab inside InsightsView, the outer hub supplies the
  // header/back chrome, so we drop our own header and full-page sizing.
  embedded?: boolean;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * This division's rate against project size.
 *
 * Replaces a ranked bar chart, which could only say which job was biggest —
 * something the estimator already knew, and which the Overview tab already
 * ranks. The question when pricing a new job is "what did jobs LIKE this one
 * cost", and answering it needs size on an axis: averaging a 2,900 SF pool
 * house with a 24,000 SF building gives a number describing neither.
 *
 * The shaded band is the middle half of the rates, so a point can be read as
 * usual or unusual rather than merely ranked. Both axes start at zero, because
 * a truncated axis on a cost rate exaggerates every difference.
 */
function DivisionScatter({
  T, metric, rows, spread, selectedId, onPick,
}: {
  T: any;
  metric: Metric;
  rows: DetailRow[];
  spread: Spread;
  selectedId: string | null;
  onPick: (id: string) => void;
}) {
  // Labelling every point collided as soon as three projects priced a division
  // within a few dollars of each other, which is the normal case rather than
  // the edge one. The dots are colour-matched to the rows underneath, so a
  // name only has to appear for the point being looked at.
  const [hoverId, setHoverId] = useState<string | null>(null);
  const W = 1000, H = 330;
  const M = { top: 18, right: 26, bottom: 46, left: 74 };
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;

  // A project with no building area has no x position. Dropping it silently
  // would be worse than saying so.
  const plotted = rows.filter((p) => p.buildingArea > 0);
  const missing = rows.length - plotted.length;

  if (plotted.length === 0) {
    return (
      <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: 20, textAlign: "center", color: T.textMuted, fontSize: 12 }}>
        No project in this division has a building area recorded, so there is nothing to plot against size.
      </div>
    );
  }

  const valOf = (p: DetailRow) =>
    metricValue(metric, { costPerSF: p.divCostPerSF, total: p.divTotal, jobSubtotal: p.subtotal });
  const comparable = metric !== "total";
  const xMax = Math.max(...plotted.map((p) => p.buildingArea)) * 1.1;
  const yMax = Math.max(...plotted.map(valOf), comparable ? spread.max : 0) * 1.18 || 1;

  const x = (sf: number) => M.left + (sf / xMax) * plotW;
  const y = (v: number) => M.top + plotH - (v / yMax) * plotH;

  const yTicks = 4;
  const fmtY = (v: number) =>
    metric === "costPerSF" ? `$${v.toFixed(0)}`
    : metric === "share" ? `${(v * 100).toFixed(0)}%`
    : `$${Math.round(v / 1000)}k`;
  const bandOn = comparable && spread.count >= 3;

  return (
    <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: "14px 16px 6px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
        <span style={{ fontSize: 10, letterSpacing: ".08em", textTransform: "uppercase", color: T.textMuted, fontWeight: 600 }}>
          {metric === "costPerSF" ? "Rate vs project size"
            : metric === "share" ? "Share of job vs project size"
            : "Division cost vs project size"}
        </span>
        {bandOn && (
          <span style={{ fontSize: 10, color: T.textMuted }}>
            shaded band = middle half ({fmtY(spread.p25)}–{fmtY(spread.p75)}{metric === "costPerSF" ? "/SF" : ""})
          </span>
        )}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img"
        aria-label="Division rate plotted against building area for each project">
        {bandOn && (
          <rect x={M.left} y={y(spread.p75)} width={plotW} height={Math.max(1, y(spread.p25) - y(spread.p75))}
            fill={T.borderAccent} opacity={0.1} />
        )}
        {bandOn && (
          <line x1={M.left} x2={W - M.right} y1={y(spread.median)} y2={y(spread.median)}
            stroke={T.borderAccent} strokeWidth={1.5} strokeDasharray="6 4" opacity={0.7} />
        )}

        {/* Y grid + labels */}
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const v = (yMax / yTicks) * i;
          return (
            <g key={i}>
              <line x1={M.left} x2={W - M.right} y1={y(v)} y2={y(v)} stroke={T.borderPrimary} strokeWidth={1} opacity={0.5} />
              <text x={M.left - 10} y={y(v) + 4} textAnchor="end" fontSize={13} fill={T.textMuted} fontFamily={FONT}>{fmtY(v)}</text>
            </g>
          );
        })}

        {/* X axis + labels */}
        <line x1={M.left} x2={W - M.right} y1={M.top + plotH} y2={M.top + plotH} stroke={T.borderSecondary} strokeWidth={1} />
        {Array.from({ length: 5 }, (_, i) => {
          const sf = (xMax / 4) * i;
          return (
            <text key={i} x={x(sf)} y={M.top + plotH + 20} textAnchor="middle" fontSize={13} fill={T.textMuted} fontFamily={FONT}>
              {Math.round(sf).toLocaleString()}
            </text>
          );
        })}
        <text x={M.left + plotW / 2} y={H - 8} textAnchor="middle" fontSize={12} fill={T.textMuted} fontFamily={FONT}
          letterSpacing="0.08em">BUILDING AREA (SF)</text>

        {plotted.map((p) => {
          const i = rows.indexOf(p);
          const on = selectedId === p.id;
          const cx = x(p.buildingArea), cy = y(valOf(p));
          const color = BAR_COLORS[i % BAR_COLORS.length];
          // Labels flip to the left near the right edge so they stay readable.
          const flip = cx > M.left + plotW * 0.72;
          return (
            <g key={p.id} onClick={() => onPick(p.id)} style={{ cursor: "pointer" }}
              onMouseEnter={() => setHoverId(p.id)} onMouseLeave={() => setHoverId(null)}>
              <title>{`${p.name} — ${p.buildingArea.toLocaleString()} SF — ${fmtMetric(metric, valOf(p))}`}</title>
              {/* A generous invisible target: a 6px dot is hard to hit. */}
              <circle cx={cx} cy={cy} r={18} fill="transparent" />
              {(on || hoverId === p.id) && <circle cx={cx} cy={cy} r={14} fill={color} opacity={0.22} />}
              <circle cx={cx} cy={cy} r={on || hoverId === p.id ? 8 : 6} fill={color} stroke={T.bgPanel} strokeWidth={2} />
              {(on || hoverId === p.id) && (
                <text x={flip ? cx - 14 : cx + 14} y={cy + 4} textAnchor={flip ? "end" : "start"}
                  fontSize={13} fontFamily={FONT} fill={T.textPrimary} fontWeight={700}
                  stroke={T.bgPanel} strokeWidth={3} paintOrder="stroke">
                  {p.name} · {fmtMetric(metric, valOf(p))}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      <div style={{ fontSize: 10, color: T.textMuted, padding: "2px 0 8px", lineHeight: 1.5 }}>
        Hover a point for the project, click to open its cost codes.
        {missing > 0 && ` ${missing} project${missing === 1 ? " has" : "s have"} no building area recorded and cannot be plotted.`}
      </div>
    </div>
  );
}

export default function BenchmarkView({ storage, user, onBack, embedded = false }: Props) {
  const [isDarkMode] = useState<boolean>(() => {
    try {
      const s = sGetRaw("oe_dark_mode");
      return s === null ? true : s === "true";
    } catch {
      return true;
    }
  });
  const T = isDarkMode ? DARK_THEME : LIGHT_THEME;

  const [winW, setWinW] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1440,
  );
  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = winW < 640;

  const isTablet = winW < 1024;

  const [projects, setProjects] = useState<ProjectBenchmark[]>([]);
  const [loading, setLoading] = useState(true);
  // By Division, not Overview. Share is the default metric and Overview cannot
  // show it -- every project is 100% of itself -- so landing there would have
  // meant the default metric was never the one on screen. The heatmap is also
  // simply the richer first look: what every job spent on every division.
  const [activeView, setActiveView] = useState<"overview" | "divisions" | "detail">("divisions");
  // Which project's cost codes are open in Division Detail. One at a time:
  // the question is "why is THIS one high", not "show me everything".
  const [openProject, setOpenProject] = useState<string | null>(null);
  // Divisions expanded into their cost codes in the heatmap. "Div 03 is 7% of
  // this job and 9% of that one" is where the question starts, not ends.
  const [openDivs, setOpenDivs] = useState<Set<string>>(new Set());
  const [selectedDivId, setSelectedDivId] = useState<string | null>(null);
  // "share" is a cost code or division as a fraction of its own job. It is the
  // only one of the three that survives comparing bespoke projects: $/SF and
  // dollars both move with how big and how expensive a house is, while the
  // SHAPE of a job -- what fraction went to finishes -- stays comparable
  // between a pool house and a mixed-use building.
  // Share is the default because it is the only metric that survives
  // comparing bespoke projects; the other two move with how big the job is.
  //
  // Stored as a preference rather than coerced on navigation: a view that
  // cannot show share falls back for rendering only, so leaving Overview
  // returns you to the metric you actually chose.
  const [metricPref, setMetricPref] = useState<Metric>("share");
  const metric: Metric =
    METRIC_OPTIONS.find((m) => m.key === metricPref)?.views.includes(activeView)
      ? metricPref
      : "costPerSF";

  // ── Load project list on mount ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await storage.listProjectsAsync();
        if (cancelled) return;
        setProjects(
          list.map((p: any) => ({
            id: p.id,
            name: p.name,
            total: p.total || 0,
            costPerSF: p.costPerSF || 0,
            buildingArea: 0,
            savedAt: p.savedAt || "",
            selected: true,
            loaded: false,
            loading: false,
            divTotals: {},
            overhead: 0,
            profit: 0,
            contingency: 0,
            subtotal: 0,
          })),
        );
      } catch (e) {
        console.error("BenchmarkView: failed to list projects", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [storage]);

  // ── Auto-load selected projects ──
  const loadProjectData = useCallback(
    async (projectId: string) => {
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, loading: true } : p)),
      );
      try {
        const state = await storage.loadProjectById(projectId);
        if (!state) {
          setProjects((prev) =>
            prev.map((p) =>
              p.id === projectId ? { ...p, loading: false, loaded: true } : p,
            ),
          );
          return;
        }

        // Compute subtotal
        let subtotal = 0;
        for (const div of state.divisions || []) {
          for (const sub of div.subcategories || []) {
            for (const id of walkItems(sub.rows || [])) {
              const li = state.itemState[id];
              if (!li || !li.included) continue;
              if (li.altType && !(li.altType === "Alt" && (li as any).altStatus === "Accepted")) continue;
              subtotal += (li.qty || 0) * (li.unitCost || 0);
            }
          }
        }

        const oh = state.overhead || 0;
        const pr = state.profit || 0;
        const cn = state.contingency || 0;
        const totalWithMarkups =
          subtotal * (1 + oh / 100) * (1 + pr / 100) * (1 + cn / 100);
        const area = state.buildingArea || 0;
        const divTotals = computeDivisionTotals(state.divisions || [], state.itemState || {}, area);

        setProjects((prev) =>
          prev.map((p) =>
            p.id === projectId
              ? {
                  ...p,
                  loading: false,
                  loaded: true,
                  subtotal,
                  total: totalWithMarkups,
                  costPerSF: area > 0 ? totalWithMarkups / area : 0,
                  buildingArea: area,
                  overhead: oh,
                  profit: pr,
                  contingency: cn,
                  divTotals,
                }
              : p,
          ),
        );
      } catch (e) {
        console.error("BenchmarkView: failed to load project", projectId, e);
        setProjects((prev) =>
          prev.map((p) =>
            p.id === projectId ? { ...p, loading: false, loaded: true } : p,
          ),
        );
      }
    },
    [storage],
  );

  useEffect(() => {
    for (const p of projects) {
      if (p.selected && !p.loaded && !p.loading) {
        loadProjectData(p.id);
        break; // load one at a time to avoid hammering
      }
    }
  }, [projects, loadProjectData]);

  // ── Derived data ──
  const selected = useMemo(() => projects.filter((p) => p.selected && p.loaded), [projects]);

  const allDivIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of selected) {
      for (const k of Object.keys(p.divTotals)) ids.add(k);
    }
    return Array.from(ids).sort();
  }, [selected]);

  const avgCostPerSF = useMemo(() => {
    if (selected.length === 0) return 0;
    return selected.reduce((s, p) => s + p.costPerSF, 0) / selected.length;
  }, [selected]);

  const avgTotal = useMemo(() => {
    if (selected.length === 0) return 0;
    return selected.reduce((s, p) => s + p.total, 0) / selected.length;
  }, [selected]);

  const spread = useMemo(() => {
    if (selected.length < 2) return 0;
    const vals = selected.map((p) => p.costPerSF);
    return Math.max(...vals) - Math.min(...vals);
  }, [selected]);

  // ── Toggle handlers ──
  const toggleProject = (id: string) =>
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, selected: !p.selected } : p)));

  const selectAll = () =>
    setProjects((prev) => prev.map((p) => ({ ...p, selected: true })));

  const selectNone = () =>
    setProjects((prev) => prev.map((p) => ({ ...p, selected: false })));

  // ── Heatmap color ──
  const heatColor = (val: number, min: number, max: number) => {
    if (max === min) return isDarkMode ? "rgba(79,209,197,0.15)" : "rgba(10,143,131,0.10)";
    const t = (val - min) / (max - min);
    if (t < 0.5) {
      const g = isDarkMode ? `rgba(74,191,106,${0.12 + t * 0.2})` : `rgba(40,152,74,${0.08 + t * 0.15})`;
      return g;
    }
    const r = isDarkMode ? `rgba(212,112,106,${0.08 + (t - 0.5) * 0.3})` : `rgba(192,40,40,${0.06 + (t - 0.5) * 0.2})`;
    return r;
  };

  // ── Divison name lookup ──
  const divName = useCallback(
    (divId: string) => {
      for (const p of selected) {
        if (p.divTotals[divId]) return p.divTotals[divId].name;
      }
      return divId;
    },
    [selected],
  );

  // ── Styles ──
  const hPad = isMobile ? 12 : isTablet ? 20 : 32;

  const cardStyle: React.CSSProperties = {
    background: T.bgPanel,
    border: `1px solid ${T.borderSecondary}`,
    borderRadius: 6,
  };

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    background: active ? T.bgHeader : "transparent",
    color: active ? T.textInverse : T.textSecondary,
    fontWeight: active ? 600 : 400,
    border: `1px solid ${active ? T.borderPrimary : T.borderSecondary}`,
    borderRadius: 3,
    padding: "6px 14px",
    fontSize: 11,
    fontFamily: FONT,
    cursor: "pointer",
    letterSpacing: ".03em",
  });

  const metricBtnStyle = (active: boolean): React.CSSProperties => ({
    background: active ? T.bgAccentSubtle : "transparent",
    color: active ? T.borderAccent : T.textMuted,
    fontWeight: active ? 600 : 400,
    border: `1px solid ${active ? T.borderAccent + "44" : T.borderSecondary}`,
    borderRadius: 3,
    padding: "4px 10px",
    fontSize: 10,
    fontFamily: FONT,
    cursor: "pointer",
  });

  // ── Render helpers ──
  const renderSpinner = () => (
    <div
      style={{
        width: 18,
        height: 18,
        border: `2px solid ${T.borderSecondary}`,
        borderTopColor: T.borderAccent,
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
      }}
    />
  );

  // ── Overview Tab ──
  const renderOverview = () => {
    const sorted = [...selected].sort((a, b) =>
      metric === "costPerSF" ? b.costPerSF - a.costPerSF : b.total - a.total,
    );
    const maxVal = sorted.length > 0 ? sorted[0][metric] : 1;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* KPI cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}>
          {[
            { label: "PROJECTS", value: String(selected.length) },
            { label: "AVG COST/SF", value: fmtSF(avgCostPerSF) },
            { label: "SPREAD", value: fmtSF(spread) },
            { label: "AVG TOTAL", value: fmt(avgTotal) },
          ].map((kpi) => (
            <div
              key={kpi.label}
              style={{ ...cardStyle, padding: "14px 20px" }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: T.textMuted,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                {kpi.label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.textPrimary }}>
                {kpi.value}
              </div>
            </div>
          ))}
        </div>

        {/* Bar chart */}
        <div style={{ ...cardStyle, padding: 16 }}>
          <div
            style={{
              fontSize: 10,
              color: T.textMuted,
              letterSpacing: ".06em",
              textTransform: "uppercase",
              marginBottom: 12,
            }}
          >
            PROJECT RANKING
          </div>
          {sorted.length === 0 ? (
            <div style={{ fontSize: 12, color: T.textMuted, padding: "20px 0", textAlign: "center" }}>
              No projects selected
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {sorted.map((p, i) => {
                const val = p[metric];
                const barPct = maxVal > 0 ? (val / maxVal) * 100 : 0;
                const color = BAR_COLORS[i % BAR_COLORS.length];
                return (
                  <div
                    key={p.id}
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <div
                      style={{
                        width: isMobile ? 80 : 140,
                        fontSize: 11,
                        color: T.textSecondary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {p.name}
                    </div>
                    <div style={{ flex: 1, height: 20, position: "relative" }}>
                      <div
                        style={{
                          width: barPct + "%",
                          height: "100%",
                          background: color,
                          borderRadius: 3,
                          // No width transition: animating width relayouts on
                          // every frame, and the usual swap to transform:
                          // scaleX would scale this 3px radius with it, giving
                          // short bars squashed lozenge ends. The bars are
                          // drawn once when the data lands, so the animation
                          // was buying nothing.
                          minWidth: 2,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: isMobile ? 70 : 90,
                        fontSize: 11,
                        fontWeight: 600,
                        color: T.textPrimary,
                        textAlign: "right",
                        flexShrink: 0,
                      }}
                    >
                      {metric === "costPerSF" ? fmtSF(val) : fmt(val)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Summary table */}
        <div style={{ ...cardStyle, overflow: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 11,
              fontFamily: FONT,
            }}
          >
            <thead>
              <tr>
                {["PROJECT", "AREA (SF)", "SUBTOTAL", "OH/PROFIT/CONT.", "TOTAL", "COST/SF"].map(
                  (h) => (
                    <th
                      key={h}
                      style={{
                        padding: "8px 12px",
                        textAlign: h === "PROJECT" ? "left" : "right",
                        fontSize: 10,
                        letterSpacing: ".06em",
                        color: T.textMuted,
                        borderBottom: `1px solid ${T.borderSecondary}`,
                        textTransform: "uppercase",
                      }}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {selected.map((p) => {
                const markup = p.total - p.subtotal;
                return (
                  <tr
                    key={p.id}
                    style={{ borderBottom: `1px solid ${T.borderSecondary}` }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = T.bgHover)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <td
                      style={{
                        padding: "8px 12px",
                        color: T.textPrimary,
                        fontWeight: 500,
                      }}
                    >
                      {p.name}
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        textAlign: "right",
                        color: T.textSecondary,
                      }}
                    >
                      {fmtArea(p.buildingArea)}
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        textAlign: "right",
                        color: T.textSecondary,
                      }}
                    >
                      {fmt(p.subtotal)}
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        textAlign: "right",
                        color: T.textMuted,
                        fontSize: 10,
                      }}
                    >
                      {fmt(markup)}
                      <span style={{ color: T.textWeak, marginLeft: 4 }}>
                        ({pct(p.overhead)}/{pct(p.profit)}/{pct(p.contingency)})
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        textAlign: "right",
                        color: T.textPrimary,
                        fontWeight: 600,
                      }}
                    >
                      {fmt(p.total)}
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        textAlign: "right",
                        color: T.borderAccent,
                        fontWeight: 600,
                      }}
                    >
                      {fmtSF(p.costPerSF)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── By Division Tab (heatmap) ──
  const renderDivisions = () => {
    if (selected.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: 40, color: T.textMuted, fontSize: 12 }}>
          No projects selected
        </div>
      );
    }

    return (
      <div style={{ ...cardStyle, overflow: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 11,
            fontFamily: FONT,
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  fontSize: 10,
                  letterSpacing: ".06em",
                  color: T.textMuted,
                  borderBottom: `1px solid ${T.borderSecondary}`,
                  textTransform: "uppercase",
                  position: "sticky",
                  left: 0,
                  background: T.bgPanel,
                  zIndex: 1,
                }}
              >
                DIVISION
              </th>
              {selected.map((p) => (
                <th
                  key={p.id}
                  style={{
                    padding: "8px 10px",
                    textAlign: "right",
                    fontSize: 10,
                    letterSpacing: ".04em",
                    color: T.textMuted,
                    borderBottom: `1px solid ${T.borderSecondary}`,
                    maxWidth: 120,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={p.name}
                >
                  {p.name}
                </th>
              ))}
              <th
                style={{
                  padding: "8px 10px",
                  textAlign: "right",
                  fontSize: 10,
                  letterSpacing: ".06em",
                  color: T.borderAccent,
                  borderBottom: `1px solid ${T.borderSecondary}`,
                  fontWeight: 700,
                }}
              >
                AVG
              </th>
            </tr>
          </thead>
          <tbody>
            {allDivIds.map((divId) => {
              const vals = selected.map((p) => {
                const dt = p.divTotals[divId];
                return dt ? metricValue(metric, { costPerSF: dt.costPerSF, total: dt.total, jobSubtotal: p.subtotal }) : 0;
              });
              const nonZero = vals.filter((v) => v > 0);
              const min = nonZero.length > 0 ? Math.min(...nonZero) : 0;
              const max = nonZero.length > 0 ? Math.max(...nonZero) : 0;
              const avg = nonZero.length > 0 ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length : 0;

              const open = openDivs.has(divId);
              const codeRows = open ? unionCostCodes(selected, divId) : [];

              return (
                <React.Fragment key={divId}>
                <tr
                  style={{
                    borderBottom: `1px solid ${T.borderSecondary}`,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setSelectedDivId(divId);
                    setActiveView("detail");
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = T.bgHover)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <td
                    style={{
                      padding: "7px 12px",
                      color: T.textPrimary,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      position: "sticky",
                      left: 0,
                      background: T.bgPanel,
                      zIndex: 1,
                    }}
                  >
                    {/* The chevron expands in place; the name still opens
                        Division Detail, so neither gesture steals the other. */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDivs((prev) => {
                          const next = new Set(prev);
                          next.has(divId) ? next.delete(divId) : next.add(divId);
                          return next;
                        });
                      }}
                      title={open ? "Hide cost codes" : "Show cost codes"}
                      aria-expanded={open}
                      style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted,
                        fontSize: 10, padding: "0 6px 0 0", fontFamily: FONT }}
                    >
                      {open ? "▾" : "▸"}
                    </button>
                    {divName(divId)}
                  </td>
                  {selected.map((p, i) => {
                    const val = vals[i];
                    return (
                      <td
                        key={p.id}
                        style={{
                          padding: "7px 10px",
                          textAlign: "right",
                          color: val > 0 ? T.textPrimary : T.textWeak,
                          background: val > 0 ? heatColor(val, min, max) : "transparent",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {val > 0 ? fmtMetric(metric, val) : "—"}
                      </td>
                    );
                  })}
                  <td
                    style={{
                      padding: "7px 10px",
                      textAlign: "right",
                      color: T.borderAccent,
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {avg > 0 ? fmtMetric(metric, avg) : "—"}
                  </td>
                </tr>

                {/* Cost codes under this division, unioned across the selected
                    projects. A gap means that project has no such code, which
                    is itself the interesting case. */}
                {open && codeRows.length === 0 && (
                  <tr>
                    <td colSpan={selected.length + 2}
                      style={{ padding: "8px 12px 8px 34px", fontSize: 11, color: T.textMuted }}>
                      No cost codes recorded under this division.
                    </td>
                  </tr>
                )}
                {open && codeRows.map((row) => {
                  const cVals = selected.map((p) => {
                    const sub = row.byProject[p.id];
                    return sub ? metricValue(metric, { costPerSF: sub.costPerSF, total: sub.total, jobSubtotal: p.subtotal }) : 0;
                  });
                  const cNon = cVals.filter((v) => v > 0);
                  const cMin = cNon.length ? Math.min(...cNon) : 0;
                  const cMax = cNon.length ? Math.max(...cNon) : 0;
                  const cAvg = cNon.length ? cNon.reduce((a, b) => a + b, 0) / cNon.length : 0;
                  return (
                    <tr key={row.key} style={{ borderBottom: `1px solid ${T.borderSecondary}55` }}>
                      <td style={{ padding: "5px 12px 5px 34px", color: T.textSecondary, fontSize: 11,
                        whiteSpace: "nowrap", position: "sticky", left: 0, background: T.bgPanel, zIndex: 1 }}>
                        {row.code && <span style={{ color: T.textMuted, marginRight: 6, fontVariantNumeric: "tabular-nums" }}>{row.code}</span>}
                        {row.name}
                      </td>
                      {selected.map((p, i) => (
                        <td key={p.id} style={{ padding: "5px 12px", textAlign: "right", fontSize: 11,
                          color: cVals[i] > 0 ? T.textSecondary : T.textWeak,
                          background: cVals[i] > 0 ? heatColor(cVals[i], cMin, cMax) : "transparent",
                          fontVariantNumeric: "tabular-nums" }}>
                          {cVals[i] > 0 ? fmtMetric(metric, cVals[i]) : "—"}
                        </td>
                      ))}
                      <td style={{ padding: "5px 12px", textAlign: "right", fontSize: 11,
                        color:T.borderAccent, fontVariantNumeric: "tabular-nums" }}>
                        {cAvg > 0 ? fmtMetric(metric, cAvg) : "—"}
                      </td>
                    </tr>
                  );
                })}
                </React.Fragment>
              );
            })}
            {/* TOTAL row */}
            <tr style={{ borderTop: `2px solid ${T.borderPrimary}` }}>
              <td
                style={{
                  padding: "8px 12px",
                  fontWeight: 700,
                  color: T.textPrimary,
                  position: "sticky",
                  left: 0,
                  background: T.bgPanel,
                  zIndex: 1,
                }}
              >
                TOTAL
              </td>
              {selected.map((p) => (
                <td
                  key={p.id}
                  style={{
                    padding: "8px 10px",
                    textAlign: "right",
                    fontWeight: 700,
                    color: T.textPrimary,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {/* Every project is all of itself, so the share column's
                      total is 100% by construction — worth showing as a check
                      that the division rows add up. */}
                  {metric === "share" ? "100.0%" : metric === "costPerSF" ? fmtSF(p.costPerSF) : fmt(p.total)}
                </td>
              ))}
              <td
                style={{
                  padding: "8px 10px",
                  textAlign: "right",
                  fontWeight: 700,
                  color: T.borderAccent,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {metric === "share" ? "100.0%" : metric === "costPerSF" ? fmtSF(avgCostPerSF) : fmt(avgTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // ── Division Detail Tab ──
  const renderDetail = () => {
    const divId = selectedDivId || allDivIds[0] || null;
    if (!divId || selected.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: 40, color: T.textMuted, fontSize: 12 }}>
          {selected.length === 0 ? "No projects selected" : "No division data available"}
        </div>
      );
    }

    const projectsWithDiv = selected
      .map((p) => {
        const dt = p.divTotals[divId];
        return dt
          ? {
              ...p,
              divTotal: dt.total,
              divCostPerSF: dt.costPerSF,
              pctOfSubtotal: p.subtotal > 0 ? (dt.total / p.subtotal) * 100 : 0,
              subs: dt.subs,
            }
          : null;
      })
      .filter(Boolean) as DetailRow[];

    const byMetric = (p: DetailRow) =>
      metricValue(metric, { costPerSF: p.divCostPerSF, total: p.divTotal, jobSubtotal: p.subtotal });
    const sorted = [...projectsWithDiv].sort((a, b) => byMetric(b) - byMetric(a));

    // The distribution the scatter reads against. Rates and shares only:
    // totals are not comparable between a pool house and a mixed-use building,
    // so calling one "high" on dollars alone would be meaningless.
    const comparable = metric !== "total";
    const valueOf = (p: DetailRow) =>
      metricValue(metric, { costPerSF: p.divCostPerSF, total: p.divTotal, jobSubtotal: p.subtotal });
    const spread = divisionSpread(
      comparable ? sorted.filter((p) => valueOf(p) > 0).map(valueOf) : [],
    );

    const avgDivTotal =
      sorted.length > 0
        ? sorted.reduce((s, p) => s + p.divTotal, 0) / sorted.length
        : 0;
    const avgDivSF =
      sorted.length > 0
        ? sorted.reduce((s, p) => s + p.divCostPerSF, 0) / sorted.length
        : 0;
    const lowSF =
      sorted.length > 0 ? Math.min(...sorted.map((p) => p.divCostPerSF)) : 0;
    const highSF =
      sorted.length > 0 ? Math.max(...sorted.map((p) => p.divCostPerSF)) : 0;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Division selector */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label
            style={{
              fontSize: 10,
              color: T.textMuted,
              letterSpacing: ".06em",
              textTransform: "uppercase",
            }}
          >
            DIVISION
          </label>
          <select
            value={divId}
            onChange={(e) => setSelectedDivId(e.target.value)}
            style={{
              background: T.bgInput,
              color: T.textPrimary,
              border: `1px solid ${T.borderSecondary}`,
              borderRadius: 3,
              padding: "5px 10px",
              fontSize: 12,
              fontFamily: FONT,
              cursor: "pointer",
              outline: "none",
            }}
          >
            {allDivIds.map((id) => (
              <option key={id} value={id}>
                {divName(id)}
              </option>
            ))}
          </select>
        </div>

        {/* Stats cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}>
          {/* The three comparison cards follow the metric on screen. Reporting
              a $/SF low and high while the table shows shares describes a view
              the user is not looking at. Average total stays in dollars
              because that is what it says it is. */}
          {[
            { label: "AVG TOTAL", value: fmt(avgDivTotal) },
            metric === "share"
              ? { label: "AVG % OF JOB", value: fmtPct(sorted.length ? sorted.reduce((a, p) => a + byMetric(p), 0) / sorted.length : 0) }
              : { label: "AVG $/SF", value: fmtSF(avgDivSF) },
            metric === "share"
              ? { label: "LOW", value: fmtPct(spread.min) }
              : { label: "LOW", value: fmtSF(lowSF) },
            metric === "share"
              ? { label: "HIGH", value: fmtPct(spread.max) }
              : { label: "HIGH", value: fmtSF(highSF) },
          ].map((kpi) => (
            <div
              key={kpi.label}
              style={{ ...cardStyle, padding: "14px 20px" }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: T.textMuted,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                {kpi.label}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary }}>
                {kpi.value}
              </div>
            </div>
          ))}
        </div>

        {/* Where this division's rate sits against project size.
            A ranked bar chart only told you which job was biggest, which you
            already knew. The question when pricing is "what did jobs LIKE this
            one cost", and that needs size on an axis. */}
        <DivisionScatter
          T={T} metric={metric} rows={sorted} spread={spread}
          selectedId={openProject} onPick={(id) => setOpenProject(id === openProject ? null : id)}
        />

        {/* Per project, expandable into its cost codes. "Div 03 is high" is
            never the end of the question; "which part of it" is. */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          {sorted.length === 0 ? (
            <div style={{ fontSize: 12, color: T.textMuted, padding: "20px 0", textAlign: "center" }}>
              No data for this division
            </div>
          ) : sorted.map((p, i) => {
            const open = openProject === p.id;
            const val = valueOf(p);
            const read = comparable ? readRate(val, spread) : "typical";
            const readColor = read === "high" ? "#fc8181" : read === "low" ? "#f6ad55" : T.textMuted;
            return (
              <div key={p.id} style={{ borderTop: i ? `1px solid ${T.borderSecondary}55` : "none" }}>
                <button
                  onClick={() => setOpenProject(open ? null : p.id)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 14px", background: open ? `${T.borderAccent}0f` : "transparent",
                    border: "none", cursor: "pointer", fontFamily: FONT, textAlign: "left" }}>
                  <span style={{ width: 10, color: T.textMuted, fontSize: 10, flexShrink: 0 }}>{open ? "▾" : "▸"}</span>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: BAR_COLORS[i % BAR_COLORS.length], flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 12, color: T.textSecondary, fontWeight: 600 }}>{p.name}</span>
                  <span style={{ fontSize: 10, color: T.textMuted, width: 82, textAlign: "right", flexShrink: 0 }}>
                    {p.buildingArea > 0 ? p.buildingArea.toLocaleString() + " SF" : "no area"}
                  </span>
                  {/* Share is already the headline number in that mode, so
                      this column carries the dollars instead of repeating it. */}
                  <span style={{ fontSize: 10, color: T.textMuted, width: 92, textAlign: "right", flexShrink: 0 }}>
                    {metric === "share" ? fmt(p.divTotal) : `${pct(p.pctOfSubtotal)} of job`}
                  </span>
                  {read !== "typical" && (
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
                      color: readColor, border: `1px solid ${readColor}55`, borderRadius: 10, padding: "1px 6px", flexShrink: 0 }}>
                      {read}
                    </span>
                  )}
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary, width: 92, textAlign: "right",
                    fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                    {fmtMetric(metric, val)}
                  </span>
                </button>

                {open && (
                  <div style={{ background: `${T.bgApp}`, padding: "4px 14px 12px 32px" }}>
                    {p.subs.length === 0 ? (
                      <div style={{ fontSize: 11, color: T.textMuted, padding: "8px 0" }}>
                        No cost codes recorded under this division.
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                        <thead>
                          <tr>
                            {["Cost Code", "Scope", "$/SF", "Total", "Share of division"].map((h, hi) => (
                              <th key={h} style={{ padding: "6px 8px", fontSize: 10, letterSpacing: ".08em",
                                textTransform: "uppercase", color: T.textMuted, fontWeight: 600,
                                textAlign: hi < 2 ? "left" : "right" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {p.subs.map((sub) => (
                            <tr key={sub.id}>
                              <td style={{ padding: "5px 8px", color: T.textMuted, fontVariantNumeric: "tabular-nums" }}>{sub.code || "—"}</td>
                              <td style={{ padding: "5px 8px", color: T.textSecondary }}>{sub.name}</td>
                              <td style={{ padding: "5px 8px", textAlign: "right", color: T.textMuted, fontVariantNumeric: "tabular-nums" }}>
                                {fmtSFPrecise(sub.costPerSF)}
                              </td>
                              <td style={{ padding: "5px 8px", textAlign: "right", color: T.textPrimary, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                                {fmt(sub.total)}
                              </td>
                              <td style={{ padding: "5px 8px", textAlign: "right", color: T.textMuted, fontVariantNumeric: "tabular-nums", width: 130 }}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "flex-end", width: "100%" }}>
                                  <span style={{ flex: 1, height: 4, background: `${T.textMuted}22`, borderRadius: 3, overflow: "hidden", maxWidth: 70 }}>
                                    <span style={{ display: "block", height: "100%", width: `${Math.round(sub.shareOfDivision * 100)}%`,
                                      background: BAR_COLORS[i % BAR_COLORS.length] }} />
                                  </span>
                                  {(sub.shareOfDivision * 100).toFixed(0)}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Sidebar ──
  const renderSidebar = () => {
    const selectedCount = selected.length;
    const anyLoading = projects.some((p) => p.selected && p.loading);
    const lowSF =
      selected.length > 0 ? Math.min(...selected.map((p) => p.costPerSF)) : 0;
    const highSF =
      selected.length > 0 ? Math.max(...selected.map((p) => p.costPerSF)) : 0;

    return (
      <div
        style={{
          width: isMobile ? "100%" : 220,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {/* Header + toggle */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{
              fontSize: 10,
              color: T.textMuted,
              letterSpacing: ".08em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            PROJECTS
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            <button
              onClick={selectAll}
              style={{
                background: "transparent",
                border: `1px solid ${T.borderSecondary}`,
                color: T.textMuted,
                fontSize: 10,
                padding: "2px 8px",
                borderRadius: 3,
                cursor: "pointer",
                fontFamily: FONT,
              }}
            >
              All
            </button>
            <button
              onClick={selectNone}
              style={{
                background: "transparent",
                border: `1px solid ${T.borderSecondary}`,
                color: T.textMuted,
                fontSize: 10,
                padding: "2px 8px",
                borderRadius: 3,
                cursor: "pointer",
                fontFamily: FONT,
              }}
            >
              None
            </button>
          </div>
        </div>

        {/* Project list */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            gap: 6,
            // Narrow layouts used to put the cards in a sideways scroller,
            // which hid half the projects behind a scrollbar nobody wants to
            // drag. They wrap instead: every project visible, no scrolling.
            flexWrap: isMobile ? "wrap" : undefined,
            overflowY: isMobile ? undefined : "auto",
            maxHeight: isMobile ? undefined : "calc(100vh - 260px)",
          }}
        >
          {projects.map((p) => (
            <div
              key={p.id}
              onClick={() => toggleProject(p.id)}
              style={{
                ...cardStyle,
                padding: "8px 10px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                borderColor: p.selected ? T.borderAccent + "66" : T.borderSecondary,
                background: p.selected ? T.bgAccentFaint : T.bgPanel,
                // Sized to share the row evenly now that the cards wrap;
                // flexShrink:0 was there to stop them squashing in the old
                // sideways scroller and would leave the last row ragged.
                flex: isMobile ? "1 1 170px" : undefined,
                minWidth: isMobile ? 140 : undefined,
                flexShrink: isMobile ? undefined : 0,
              }}
            >
              {/* Checkbox */}
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  border: `1.5px solid ${p.selected ? T.borderAccent : T.borderSecondary}`,
                  background: p.selected ? T.borderAccent : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {p.selected && (
                  <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-6" stroke={T.bgPanel} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: T.textPrimary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.name}
                </div>
                <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1 }}>
                  {p.loading ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {renderSpinner()} Loading...
                    </span>
                  ) : p.loaded ? (
                    metric === "costPerSF" ? fmtSF(p.costPerSF) : fmt(p.total)
                  ) : (
                    "Not loaded"
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary card */}
        {selectedCount > 0 && (
          <div
            style={{
              ...cardStyle,
              padding: "10px 12px",
              background: T.bgAccentFaint,
              borderColor: T.borderAccent + "33",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: T.textMuted,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                marginBottom: 6,
                fontWeight: 600,
              }}
            >
              SUMMARY
            </div>
            <div style={{ fontSize: 11, color: T.textSecondary, display: "flex", flexDirection: "column", gap: 3 }}>
              <div>
                <span style={{ color: T.textMuted }}>Projects: </span>
                <span style={{ color: T.textPrimary, fontWeight: 600 }}>{selectedCount}</span>
                {anyLoading && <span style={{ color: T.textWeak, marginLeft: 4 }}>(loading...)</span>}
              </div>
              <div>
                <span style={{ color: T.textMuted }}>Avg $/SF: </span>
                <span style={{ color: T.borderAccent, fontWeight: 600 }}>{fmtSF(avgCostPerSF)}</span>
              </div>
              <div>
                <span style={{ color: T.textMuted }}>Range: </span>
                <span style={{ color: T.textPrimary }}>{fmtSF(lowSF)} - {fmtSF(highSF)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Main render ──
  return (
    <div
      style={{
        fontFamily: FONT,
        background: embedded ? "transparent" : T.bgApp,
        color: T.textPrimary,
        minHeight: embedded ? undefined : "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Spinner keyframes */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ── Header (omitted when embedded — InsightsView supplies the chrome) ── */}
      {!embedded && (
      <div
        style={{
          background: T.bgHeader,
          borderBottom: `1px solid ${T.borderPrimary}`,
          height: 52,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: `0 ${hPad}px`,
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={onBack}
            title="Back to projects"
            style={{
              background: "none",
              border: `1px solid ${T.textInverse}44`,
              color: T.textInverse,
              cursor: "pointer",
              padding: "5px 12px",
              borderRadius: 3,
              fontSize: 12,
              fontFamily: FONT,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = T.borderAccent)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = `${T.textInverse}44`)}
          >
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 1L1 9h3v8h5v-5h2v5h5V9h3L10 1z" />
            </svg>
          </button>
          <span
            style={{
              fontSize: isMobile ? 14 : 16,
              fontWeight: 700,
              color: T.textInverse,
            }}
          >
            Portfolio Benchmarks
          </span>
          <span
            style={{
              fontSize: 10,
              color: T.textWeak,
              letterSpacing: ".08em",
              flexShrink: 0,
            }}
          >
            ANALYSIS
          </span>
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: T.textWeak,
            letterSpacing: ".06em",
          }}
        >
          OpenEstimate
        </div>
      </div>
      )}

      {/* ── Body ── */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: embedded ? 0 : `20px ${hPad}px`,
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "flex",
            gap: 20,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {/* Sidebar */}
          {renderSidebar()}

          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Tab bar + metric toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", gap: 4 }}>
                {(
                  [
                    { key: "overview", label: "Overview" },
                    { key: "divisions", label: "By Division" },
                    { key: "detail", label: "Division Detail" },
                  ] as const
                ).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveView(tab.key)}
                    style={tabBtnStyle(activeView === tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {METRIC_OPTIONS.filter((m) => m.views.includes(activeView)).map((m) => (
                  <button key={m.key} onClick={() => setMetricPref(m.key)} style={metricBtnStyle(metric === m.key)}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Loading state */}
            {loading ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 60,
                  gap: 10,
                }}
              >
                {renderSpinner()}
                <span style={{ fontSize: 12, color: T.textMuted }}>
                  Loading projects...
                </span>
              </div>
            ) : projects.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 60,
                  color: T.textMuted,
                  fontSize: 13,
                }}
              >
                No projects found. Save some estimates to compare them here.
              </div>
            ) : (
              <>
                {activeView === "overview" && renderOverview()}
                {activeView === "divisions" && renderDivisions()}
                {activeView === "detail" && renderDetail()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
